import React, { useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RestoreIcon from "@mui/icons-material/Restore";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { API_URLS } from "../../config/apiUrls";

interface Subcategoria {
  id: string;
  name: string;
  isDeleted: boolean;
}

interface Props {
  subcategoria: Subcategoria;
  isLoading: boolean;
  token: string;
  onChanged: () => void;
}

const CategoriasCardSubcategoria: React.FC<Props> = ({
  subcategoria,
  isLoading,
  token,
  onChanged,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState(subcategoria.name);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const request = async (path: string, method: string, body?: object) => {
    const response = await fetch(path, {
      method,
      headers: {
        ...(body ? { "Content-Type": "application/json" } : {}),
        Authorization: `Bearer ${token}`,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    if (!response.ok) throw new Error("Não foi possível atualizar a subcategoria.");
  };

  const salvar = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      await request(`${API_URLS.subcategorias}/${subcategoria.id}`, "PUT", { name });
      setIsEditing(false);
      onChanged();
    } catch (error) {
      console.error("Erro ao editar subcategoria:", error);
    }
  };

  const excluir = async () => {
    try {
      await request(`${API_URLS.subcategorias}/${subcategoria.id}`, "DELETE");
      setConfirmDelete(false);
      onChanged();
    } catch (error) {
      console.error("Erro ao excluir subcategoria:", error);
    }
  };

  const restaurar = async () => {
    try {
      await request(`${API_URLS.subcategorias}/${subcategoria.id}/restore`, "POST");
      onChanged();
    } catch (error) {
      console.error("Erro ao restaurar subcategoria:", error);
    }
  };

  return (
    <Card variant="outlined" sx={{ minWidth: 190, opacity: subcategoria.isDeleted ? 0.72 : 1 }}>
      <CardContent sx={{ "&:last-child": { pb: 2 } }}>
        {isEditing ? (
          <TextField
            size="small"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
            autoFocus
          />
        ) : (
          <Typography variant="body2">{subcategoria.name}</Typography>
        )}

        <Box display="flex" gap={1} mt={2}>
          {subcategoria.isDeleted ? (
            <Button
              size="small"
              startIcon={<RestoreIcon />}
              onClick={restaurar}
              disabled={isLoading}
            >
              Restaurar
            </Button>
          ) : isEditing ? (
            <>
              <Button size="small" onClick={salvar} disabled={!newName.trim()}>
                Salvar
              </Button>
              <Button
                size="small"
                color="secondary"
                onClick={() => {
                  setIsEditing(false);
                  setNewName(subcategoria.name);
                }}
              >
                Cancelar
              </Button>
            </>
          ) : (
            <>
              <Button size="small" onClick={() => setIsEditing(true)} aria-label="Editar">
                <EditIcon fontSize="small" />
              </Button>
              <Button
                size="small"
                color="error"
                onClick={() => setConfirmDelete(true)}
                aria-label="Excluir"
              >
                <DeleteIcon fontSize="small" />
              </Button>
            </>
          )}
        </Box>
      </CardContent>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir subcategoria?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            A subcategoria <strong>{subcategoria.name}</strong> deixará de aparecer no app.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancelar</Button>
          <Button onClick={excluir} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default CategoriasCardSubcategoria;
